import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radar, Phone, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BackButton from "@/components/hola/BackButton";
import PasswordInput from "@/components/hola/PasswordInput";
import api from "@/lib/api";
import { toast } from "sonner";

export default function MerchantLogin() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [pickerMerchants, setPickerMerchants] = useState(null); // array cuando hay que elegir

  const finishLogin = (data) => {
    localStorage.setItem("holaradar_merchant_token", data.token);
    toast.success("Bienvenido de vuelta");
    navigate("/dashboard");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { phone, password });
      if (data.requires_selection) {
        setPickerMerchants(data.merchants || []);
      } else {
        finishLogin(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  const selectMerchant = async (merchant_id) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/select", { phone, password, merchant_id });
      finishLogin(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo entrar a ese comercio");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left panel */}
      <div className="hidden md:flex flex-col justify-between bg-[#0055FF] text-white p-12">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-white text-[#0055FF] grid place-items-center">
            <Radar className="w-5 h-5" />
          </div>
          <span className="font-heading font-extrabold text-xl">HOLA RADAR</span>
        </Link>
        <div>
          <h2 className="font-heading text-4xl font-extrabold leading-tight">
            Entra a tu radar de clientes.
          </h2>
          <p className="mt-4 text-blue-100 max-w-md">
            Gestiona ofertas, mide matches y deja que la IA cierre ventas mientras tú atiendes tu negocio.
          </p>
        </div>
        <div className="text-xs text-blue-200">© 2026 HOLA RADAR</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="mb-6 -ml-2">
            <BackButton to="/" label="Regresar al inicio" testid="login-back-btn" />
          </div>
          <Link to="/" className="md:hidden flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#0055FF] text-white grid place-items-center">
              <Radar className="w-5 h-5" />
            </div>
            <span className="font-heading font-extrabold text-lg">HOLA RADAR</span>
          </Link>
          <h1 className="font-heading text-3xl font-extrabold">Entrar a tu comercio</h1>
          <p className="text-sm text-neutral-500 mt-1">Usa tu teléfono o Google.</p>

          {pickerMerchants ? (
            <div className="mt-8 space-y-3" data-testid="login-picker">
              <div className="rounded-2xl bg-[#FFF0F7] border border-[#FF007A]/25 p-4">
                <h2 className="font-heading font-extrabold text-base">Elige el comercio</h2>
                <p className="text-xs text-neutral-600 mt-1">Este celular tiene {pickerMerchants.length} negocios registrados. Selecciona con cuál quieres entrar.</p>
              </div>
              <div className="space-y-2">
                {pickerMerchants.map((m) => (
                  <button
                    key={m.merchant_id}
                    onClick={() => selectMerchant(m.merchant_id)}
                    disabled={loading}
                    className="w-full text-left border border-neutral-200 hover:border-[#0055FF] hover:bg-[#0055FF]/5 rounded-2xl px-4 py-3 flex items-center gap-3 transition-colors"
                    data-testid={`login-pick-${m.merchant_id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-neutral-100 grid place-items-center shrink-0 overflow-hidden">
                      {m.picture ? (
                        <img src={m.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Radar className="w-5 h-5 text-neutral-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">{m.business_name || "(sin nombre)"}</div>
                      <div className="text-xs text-neutral-500 capitalize">{m.category || "general"}</div>
                    </div>
                    <LogIn className="w-4 h-4 text-neutral-400" />
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => { setPickerMerchants(null); setPassword(""); }}
                className="w-full text-center text-xs text-neutral-500 mt-2 hover:text-neutral-800"
                data-testid="login-picker-cancel"
              >
                Cancelar y volver
              </button>
            </div>
          ) : (
          <>
          <Button
            onClick={handleGoogle}
            variant="outline"
            className="mt-6 w-full h-12 rounded-full border-neutral-300 font-semibold"
            data-testid="google-login-btn"
          >
            <GoogleIcon /> Continuar con Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs text-neutral-400">
            <div className="h-px bg-neutral-200 flex-1"></div>
            <span>o con celular</span>
            <div className="h-px bg-neutral-200 flex-1"></div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="phone">Celular <span className="text-neutral-400 font-normal">(10 dígitos)</span></Label>
              <div className="mt-1.5 relative">
                <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  id="phone"
                  data-testid="login-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="5512345678"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  className="pl-9 h-11 rounded-xl"
                  required
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Contraseña</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-[#FF007A] font-bold hover:underline"
                  data-testid="login-forgot-link"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <PasswordInput
                id="password"
                testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className="mt-1.5 h-11 rounded-xl"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-12 rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold"
            >
              <LogIn className="w-4 h-4" /> {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <p className="text-sm text-neutral-500 mt-6 text-center">
            ¿No tienes cuenta?{" "}
            <Link to="/register" className="text-[#0055FF] font-bold" data-testid="link-register">Registrar comercio</Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.4 2 30.1 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.6c-.5 2.8-2.1 5.2-4.5 6.7l7.1 5.5c4.2-3.9 6.7-9.6 6.7-16.7z" />
      <path fill="#FBBC05" d="M10.5 28.7c-.5-1.4-.8-2.9-.8-4.5s.3-3.1.8-4.5l-7.9-6.1C.9 17.3 0 20.5 0 24s.9 6.7 2.6 10.5l7.9-5.8z" />
      <path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.5l-7.1-5.5c-2 1.4-4.6 2.2-7.9 2.2-6.3 0-11.6-4.1-13.5-9.7l-7.9 5.8C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}
